<!-- View controls: who it is from (All / Team / System + sender), then the read state. -->
<div class="notif-feed-controls">
  <div class="notif-filter-row">
    <div class="notif-seg" role="group" aria-label="Show notifications from">
      @for (tab of sourceTabs; track tab.id) {
        <button type="button" [class.on]="source === tab.id" [attr.aria-pressed]="source === tab.id"
                (click)="setSource(tab.id)">
          {{ tab.label }}
          @if (countsLoaded) {
            <span class="notif-seg-count">{{ counts[tab.id] }}</span>
          }
        </button>
      }
    </div>

    <!-- Sender menu — the same staff list as the @-mention picker, with a search
         box, each person's photo (initials when there is none) and how many of
         your notifications from them are unread. GatherCo System is its own
         tab, so it is not offered here. -->
    <div class="notif-sender" (click)="$event.stopPropagation()">
      <button type="button" class="notif-sender-btn" [class.set]="!!senderId"
              [attr.aria-expanded]="senderOpen" aria-haspopup="listbox" (click)="toggleSenderMenu()">
        @if (selectedSender; as person) {
          @if (person.picture && !failedAvatars.has(person.id)) {
            <img class="notif-sender-btn-av" [src]="getUserImage(person.picture)" alt=""
                 (error)="onSenderAvatarError(person.id)"/>
          } @else {
            <span class="notif-sender-btn-av notif-sender-initials" aria-hidden="true">{{ person.initials }}</span>
          }
        } @else {
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        }
        <span class="notif-sender-val">{{ senderLabel }}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
      </button>
      @if (senderOpen) {
        <div class="notif-sender-menu" role="dialog" aria-label="Notifications from" (keydown.escape)="closeSenderMenu()">
          <input #senderSearch type="text" class="notif-sender-search" placeholder="Search people..."
                 aria-label="Search people" [value]="senderQuery" (input)="senderQuery = $any($event.target).value"/>
          <div class="notif-sender-opts" role="listbox">
            <button type="button" class="notif-sender-opt" role="option" [class.on]="!senderId"
                    [attr.aria-selected]="!senderId" (click)="pickSender(null)">
              <span class="notif-sender-av notif-sender-everyone" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <span class="notif-sender-name">Everyone</span>
            </button>
            @for (person of filteredSenders; track person.id) {
              <button type="button" class="notif-sender-opt" role="option" [class.on]="senderId === person.id"
                      [attr.aria-selected]="senderId === person.id" (click)="pickSender(person.id)">
                @if (person.picture && !failedAvatars.has(person.id)) {
                  <img class="notif-sender-av" [src]="getUserImage(person.picture)" alt=""
                       (error)="onSenderAvatarError(person.id)"/>
                } @else {
                  <span class="notif-sender-av notif-sender-initials" aria-hidden="true">{{ person.initials }}</span>
                }
                <span class="notif-sender-name">{{ person.name }}</span>
                @if (unreadFrom(person.id); as unread) {
                  <span class="notif-sender-count" [attr.aria-label]="unread + ' unread'">{{ unread }}</span>
                }
              </button>
            } @empty {
              @if (senderQuery.trim()) {
                <p class="notif-sender-none">No one matches "{{ senderQuery.trim() }}".</p>
              }
            }
          </div>
        </div>
      }
    </div>
  </div>

  <div class="notif-controls">
    @if (!showCompleted) {
      <button type="button" class="notif-toggle" [class.on]="isToggledOn" (click)="onChangeToggleIcon()"
              role="switch" [attr.aria-checked]="isToggledOn">
        <span class="notif-track"><span class="notif-knob"></span></span>
        Only show unread
      </button>
      <!-- While a tab or a person narrows the list, this link clears them instead. -->
      @if (isFiltered) {
        <button type="button" class="notif-link" (click)="clearFilters()">Clear filters</button>
      } @else {
        <button type="button" class="notif-link" (click)="setCompleted(true)">Recent Completed</button>
      }
    } @else {
      <!-- Recent Completed swaps the list; the unread toggle has nothing to act on here,
           and the way back is the only control (no "Recent Completed" label once it's open). -->
      <button type="button" class="notif-back" (click)="setCompleted(false)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        Back to notifications
      </button>
    }
  </div>
</div>

<!-- List -->
<div class="notif-list">
  <!-- Veil while a view is being fetched (first open, tab, sender, toggle, Recent Completed). -->
  @if (isLoading) {
    <div class="notif-loader" role="status" aria-label="Loading notifications">
      <span class="notif-spinner"></span>
    </div>
  }

  @for (notification of rows; track notification.id) {
    <a class="notif-row" [class.unread]="isUnread(notification)"
       (click)="redirectToModule(notification.note_reaction_id ? notification.noteReaction : notification)">
      <!-- unread accent rail -->
      <span class="notif-rail" [attr.data-type]="notifType(notification)"></span>

      <!-- avatar + corner type badge -->
      <span class="notif-avatar-wrap">
        <span class="notif-avatar" [attr.data-type]="notifType(notification)">
          <img [src]="getUserImage(notification?.fromUser?.profile_picture)"
               (error)="helperFunction.getErrorImage($event, 'default_profile.png')"
               [alt]="getNotificationSenderName(notification?.fromUser)"/>
        </span>
        <span class="notif-badge" [attr.data-type]="notifType(notification)"
              [tooltip]="typeLabel(notifType(notification))" placement="top" container="body"
              containerClass="srd-tip-bubble notif-tip-bubble">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            @switch (notifType(notification)) {
              @case ('order') {
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              }
              @case ('quote') {
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
              }
              @case ('sample') {
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/>
              }
              @case ('mention') {
                <path d="M12 2a10 10 0 1 0 4 19.2"/>
                <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.9 7.9"/>
                <circle cx="12" cy="12" r="4"/>
              }
              @default {
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              }
            }
          </svg>
        </span>
      </span>

      <!-- content -->
      <div class="notif-body">
        <!-- Received time; under Recent Completed, the completed time (modified_at),
             left blank on old rows that never recorded one. -->
        <div class="notif-line1">
          <span class="notif-title">{{ notifTitle(notification) }}</span>
          <span class="notif-time">{{ (showCompleted ? notification.modified_at : notification.created_at) | dateAgo }}</span>
        </div>
        <div class="notif-text" [innerHTML]="notifMessage(notification)"></div>

        @if (notifChip(notification); as chip) {
          <span class="notif-chip" [attr.data-type]="chip.kind">{{ chip.label }} · {{ chip.ref }}</span>
        }
      </div>

      <!-- per-item actions: unread dot, ✓ read / unread, 👍 -->
      <div class="notif-actions">
        @if (isUnread(notification)) {
          <span class="notif-dot" tooltip="Unread" placement="left" container="body"
                containerClass="srd-tip-bubble notif-tip-bubble"></span>
          <button type="button" class="notif-act" (click)="setRead($event, notification, true)"
                  [disabled]="readSaving(notification)"
                  tooltip="Mark as read" placement="left" container="body" aria-label="Mark as read"
                  containerClass="srd-tip-bubble notif-tip-bubble">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          </button>
        } @else {
          <button type="button" class="notif-act" (click)="setRead($event, notification, false)"
                  [disabled]="readSaving(notification)"
                  tooltip="Mark as unread" placement="left" container="body" aria-label="Mark as unread"
                  containerClass="srd-tip-bubble notif-tip-bubble">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/>
            </svg>
          </button>
        }
        <!-- Like the linked note (also marks the row as read). Hidden for
             notifications with no note behind them (e.g. product comments). -->
        @if (canLike(notification)) {
          <button type="button" class="notif-act notif-like"
                  [class.liked]="isLiked(notification)" (click)="toggleLike($event, notification)"
                  [tooltip]="isLiked(notification) ? 'Unlike' : 'Like'" placement="left" container="body"
                  containerClass="srd-tip-bubble notif-tip-bubble"
                  [attr.aria-label]="isLiked(notification) ? 'Unlike' : 'Like'">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z"/>
              <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
            </svg>
          </button>
        }
      </div>
    </a>
  }

  <!-- Recent Completed only: the rows after the last one shown, from the server.
       The main list stops at the latest 20; "View all notifications" has the rest. -->
  @if (showCompleted && rows.length && remaining > 0) {
    <div class="notif-more">
      <button type="button" class="notif-more-btn" [disabled]="isLoadingMore" (click)="loadMore()">
        {{ isLoadingMore ? 'Loading…' : 'Load more' }}
      </button>
    </div>
  }

  <!-- Empty state -->
  @if (!rows.length && !isLoading) {
    <div class="notif-empty">
      <div class="notif-empty-ic">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
             stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
      </div>
      <p class="notif-empty-title">{{ emptyTitle }}</p>
      <p class="notif-empty-sub">{{ emptySub }}</p>
    </div>
  }
</div>
